/**
 * ADMIN: member management
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { isFunction, isString, toInteger } from "lodash";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Stack,
  Checkbox,
  FormControlLabel,
  Button,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  DialogActions,
  DialogContent as MuiDialogContent,
  DialogContentText,
  Snackbar,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Chip,
  CardActions,
} from "@mui/material";
import {
  Close as CloseIcon,
  PictureAsPdf as PictureAsPdfIcon,
  FileDownload as FileDownloadIcon,
  FilterAlt as FilterAltIcon,
  FilterAltOff as FilterAltOffIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Block as BlockIcon,
  PersonAdd as PersonAddIcon,
  TableChart as TableChartIcon,
  DeveloperMode as DataIcon,
  DeleteForever as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Info as InfoIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  AttachMoney as AttachMoneyIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { TimelineDot } from "@mui/lab";
import {
  getOnlineApplications,
  getMembershipPayments,
  getMeetingPayments,
  getMembershipTypes,
  getPaymentStatuses,
} from "../actions/membership.actions";
import { nanoid } from "nanoid";
import { exportDataToCsv } from "@smfa/utils/src/common/csv";

import SearchControls from "../components/common/SearchControls";
import DataTable from "../components/common/DataTable";
import ResponsiveDataTable from "../components/common/ResponsiveDataTable";
import TableActionSplitButton from "../components/common/TableActionSplitButton";
import TablePagination from "../components/common/TablePagination";
import { createConfiguredClient } from "@smfa/utils/src";
import { TokenManager } from "@smfa/utils/src";
import TableCellCopy from "../components/common/TableCellCopy";
import ToolBody from "../components/common/ToolBody";
import PdfDownloadBackdrop from "../components/common/PdfDownloadBackdrop";
import {
  getLegacyMembershipYearLabel,
  getMembershipResolutionLabel,
  getMembershipTermLabel,
  formatDate,
  isCurrentActiveMembershipPayment,
  isCurrentActivePaidMembership,
  shouldShowLegacyMembershipYear,
} from "../utils/membershipDisplay";
import {
  pdfDownloadFilename,
  zipDownloadFilename,
} from "../utils/downloadFilename";

const axios = createConfiguredClient({ isAdminSite: true });
const PDF_ZIP_TIMEOUT = 5 * 60 * 1000;
const PDF_DOWNLOAD_TIMEOUT = 2 * 60 * 1000;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

const cssTimelineDotLegendBox = {
  display: "flex",
  justifyContent: "left",
  alignItems: "center",
};

/**
 * @returns {JSX.Element}
 * @component
 */
const Members = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const [downloadDisabled, setDownloadDisabled] = useState(true);
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [downloadAllZipLoading, setDownloadAllZipLoading] = useState(false);
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const [pdfDownloadMessage, setPdfDownloadMessage] = useState("");

  // Pagination state
  const [pageNo, setPageNo] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(1);

  // Search state
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("email_id");
  const [showAllPending, setShowAllPending] = useState(false);
  const hasInitializedListRef = useRef(false);

  // User account management state
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [fetchingTypes, setFetchingTypes] = useState(false);
  const [editedFields, setEditedFields] = useState({
    email: "",
    plan_type: "",
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [confirmFieldTouched, setConfirmFieldTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [emailVerification, setEmailVerification] = useState("");
  const [emailVerificationError, setEmailVerificationError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // Payment info management state
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [fetchingPaymentStatuses, setFetchingPaymentStatuses] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [membershipPayments, setMembershipPayments] = useState([]);
  const [editedPaymentInfo, setEditedPaymentInfo] = useState({
    payment_status: "",
    txn_id: "",
    payment_amount: "",
  });
  // eslint-disable-next-line no-unused-vars
  const [meetingPayments, setMeetingPayments] = useState([]);
  const [resetToken, setResetToken] = useState("");
  const [showResetTokenDialog, setShowResetTokenDialog] = useState(false);
  const [tokenCopySuccess, setTokenCopySuccess] = useState(false);
  const [showRawDataDialog, setShowRawDataDialog] = useState(false);
  const [rawDataCopySuccess, setRawDataCopySuccess] = useState(false);
  const memberEmailLookupRef = useRef("");

  const token = TokenManager.getToken();
  axios.defaults.headers.common["Authorization"] = token
    ? `Bearer ${token}`
    : null;

  /**
   * Fetch form and other options where only the server should be trusted for template values
   */
  const fetchOptionData = useCallback(async () => {
    if (membershipTypes.length > 0 || paymentStatuses.length > 0) {
      return;
    }
    setFetchingTypes(true);
    setFetchingPaymentStatuses(true);
    const [_membershipTypes, _paymentStatuses] = await Promise.all([
      getMembershipTypes(),
      getPaymentStatuses(),
    ]);

    if (_membershipTypes?.data?.status) {
      setMembershipTypes(_membershipTypes.data.data || []);
    }
    if (_paymentStatuses?.data?.status) {
      setPaymentStatuses(_paymentStatuses.data.data || []);
    }
    setFetchingTypes(false);
    setFetchingPaymentStatuses(false);
  }, [membershipTypes, paymentStatuses]);

  /**
   * For the user information dialog, fetch various payment details
   * @param {Object} param0.userId - User ID
   * @param {Object} param0.applicationId - Application ID
   * @param {Object} param0.email - Email address
   * @returns {Promise<void>}
   */
  const fetchPayments = useCallback(
    async ({ userId, applicationId, email }) => {
      try {
        const [_membershipPayments, _meetingPayments] = await Promise.all([
          getMembershipPayments({ userId: userId || email, applicationId }),
          getMeetingPayments({ userId: userId || email }),
        ]);
        log.debug("fetchPayments() results:", {
          _membershipPayments,
          _meetingPayments,
        });
        if (_membershipPayments?.data?.status) {
          setMembershipPayments(_membershipPayments.data.data || []);
        }
        if (_meetingPayments?.data?.status) {
          setMeetingPayments(_meetingPayments.data.data || []);
        }
      } catch (error) {
        log.error("Failed to fetch payments for selected user:", error);
      }
    },
    [],
  );

  const fetchApplications = useCallback(
    (params = {}) => {
      setLoading(true);

      const requestParams = {
        pageNo: params.pageNo || pageNo,
        perPage: params.perPage || perPage,
        searchText:
          params.searchText !== undefined ? params.searchText : searchText,
        searchType: params.searchType || searchType,
        showAllPending:
          params.showAllPending !== undefined
            ? params.showAllPending
            : showAllPending,
      };

      // Log the search parameters for debugging
      log.debug("Search parameters:", requestParams);

      getOnlineApplications(requestParams)
        .then((res) => {
          if (res.data.status) {
            setApplications(res.data.data.appliactions || []);
            setTotalCount(res.data.data.totalCount || 0);
            setTotalPage(res.data.data.totalPage || 1);

            // Update pagination state if provided in params
            if (params.pageNo) setPageNo(params.pageNo);
            if (params.perPage) setPerPage(params.perPage);
            if (params.searchText !== undefined)
              setSearchText(params.searchText);
            if (params.searchType) setSearchType(params.searchType);
            if (params.showAllPending !== undefined)
              setShowAllPending(Boolean(params.showAllPending));
          }
        })
        .catch((error) => {
          log.error("Failed to fetch applications:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [pageNo, perPage, searchText, searchType, showAllPending],
  );

  // Initialize data
  useEffect(() => {
    if (hasInitializedListRef.current) {
      return;
    }
    hasInitializedListRef.current = true;
    fetchApplications();
    // No need to fetch membership types until the dialog is opened
  }, [fetchApplications]);

  // Handle checkbox selection
  const handleSelection = useCallback((id) => {
    setSelectedIds((prevIds) => {
      // Check if ID already exists in array
      const exists = prevIds.includes(id);

      // If exists, remove it; otherwise add it
      const newIds = exists
        ? prevIds.filter((prevId) => prevId !== id)
        : [...prevIds, id];

      // Update download button state based on selection count
      setDownloadDisabled(newIds.length === 0 || newIds.length > 25);

      return newIds;
    });
  }, []);

  // Handle bulk selection
  const handleBulkSelection = useCallback(
    (checked) => {
      if (checked) {
        const newIds = applications.map((app) => app.id);
        setSelectedIds(newIds.length > 25 ? newIds.slice(0, 25) : newIds);
        setDownloadDisabled(false);
      } else {
        setSelectedIds([]);
        setDownloadDisabled(true);
      }
    },
    [applications],
  );

  // Download PDF for a single application
  const downloadPdf = useCallback(
    async (id, user = {}) => {
      setPdfDownloadLoading(true);
      setPdfDownloadMessage("Generating PDF...");

      try {
        const res = await axios.get(`/onlineapplicationpdf/${id}`, {
          headers: {
            Authorization: token ? "Bearer " + token : null,
          },
          responseType: "blob",
          timeout: PDF_DOWNLOAD_TIMEOUT,
        });
        const filename = pdfDownloadFilename("Members", user?.email_id, id);

        // Create blob and download
        const blob = new Blob([res.data], { type: res.data.type });
        const link = document.createElement("a");
        const objectUrl = window.URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
      } catch (error) {
        log.error("Error downloading PDF:", error);
      } finally {
        setPdfDownloadLoading(false);
        setPdfDownloadMessage("");
      }
    },
    [token],
  );

  const downloadApplicationsZip = useCallback(
    async (applicationIds, filename) => {
      setPdfDownloadLoading(true);
      setPdfDownloadMessage("Generating PDF ZIP...");

      try {
        const response = await axios.post(
          "/onlineapplicationpdfall",
          { ids: applicationIds },
          {
            headers: {
              Authorization: token ? "Bearer " + token : null,
            },
            responseType: "blob",
            timeout: PDF_ZIP_TIMEOUT,
          },
        );
        const blob = new Blob([response.data], { type: response.data.type });
        const link = document.createElement("a");
        const objectUrl = window.URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
      } finally {
        setPdfDownloadLoading(false);
        setPdfDownloadMessage("");
      }
    },
    [token],
  );

  // Download selected user data as zip
  const downloadSelectedAsZip = useCallback(() => {
    if (selectedIds.length === 0 || selectedIds.length > 25) {
      return;
    }

    downloadApplicationsZip(
      selectedIds,
      zipDownloadFilename("Members"),
    ).catch((error) => {
      log.error("Error downloading zip:", error);
    });
  }, [downloadApplicationsZip, selectedIds]);

  // Format applicant name
  const formatName = useCallback((item) => {
    let parts = [];
    const _keys = ["title", "first_name", "middle_name", "last_name"];
    _keys.forEach((key) => {
      if (item[key]) {
        parts.push(item[key]);
      }
    });
    return parts.join(" ");
  }, []);

  // Handle input changes for editable fields
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Map application_email field name to email in editedFields
    if (name === "application_email") {
      setEditedFields((prev) => ({
        ...prev,
        email: value,
      }));
    } else {
      setEditedFields((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }, []);

  // Validate password - update to detect mismatches immediately when both fields are filled
  const validatePassword = useCallback(() => {
    if (password && password.length <= 8) {
      setPasswordError("Password must be at least 8 characters long");
      return false;
    }

    // Show mismatch error as soon as both fields have values
    if (password && confirmPassword && password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }

    setPasswordError("");
    return true;
  }, [password, confirmPassword]);

  // Handle confirm password change to validate in real-time
  const handleConfirmPasswordChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setConfirmPassword(newValue);
      setConfirmFieldTouched(true);

      // Validate immediately when both passwords are filled
      if (password && newValue) {
        if (password !== newValue) {
          setPasswordError("Passwords do not match");
        } else {
          setPasswordError("");
        }
      }
    },
    [password],
  );

  // function to handle confirm password field blur
  const handleConfirmPasswordBlur = useCallback(() => {
    setConfirmFieldTouched(true);
    validatePassword();
  }, [validatePassword]);

  // copy password to clipboard
  const copyPasswordToClipboard = useCallback(() => {
    navigator.clipboard.writeText(password).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      () => {
        log.error("Failed to copy password");
      },
    );
  }, [password]);

  // toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  // copy token to clipboard
  // eslint-disable-next-line no-unused-vars
  const copyTokenToClipboard = useCallback(() => {
    navigator.clipboard.writeText(resetToken).then(
      () => {
        setTokenCopySuccess(true);
        setTimeout(() => setTokenCopySuccess(false), 2000);
      },
      () => {
        log.error("Failed to copy reset token");
      },
    );
  }, [resetToken]);

  // copy raw data to clipboard
  const copyRawDataToClipboard = useCallback(() => {
    const rawData = JSON.stringify(detailItem, null, 2);
    navigator.clipboard.writeText(rawData).then(
      () => {
        setRawDataCopySuccess(true);
        setTimeout(() => setRawDataCopySuccess(false), 2000);
      },
      () => {
        log.error("Failed to copy raw data");
      },
    );
  }, [detailItem]);

  // Update user account - Update this function to include payment info updates
  const updateUserAccount = useCallback(() => {
    if (password && !validatePassword()) return;

    // Validate payment amount if provided
    if (
      editedPaymentInfo.payment_amount &&
      isNaN(parseFloat(editedPaymentInfo.payment_amount))
    ) {
      setSubmitError("Payment amount must be a valid number");
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    // Create updateData object with user account fields
    const updateData = {
      user_id: detailItem.user_data?.user_id || detailItem.user_data?.id,
      application_id: detailItem.id,
      email: editedFields.email,
      plan_type: editedFields?.plan_type,
      reset_token: resetToken,
    };

    // If plan_type is being assigned, set payment_status to complimentary
    if (editedFields?.plan_type && editedFields.plan_type.trim()) {
      updateData.payment_status = "complimentary";
    }

    if (password) {
      updateData.password = password;
    }

    // First update user account if needed
    const updateUserPromise = detailItem.has_user_account
      ? axios.post("/update-application-user", updateData)
      : Promise.resolve({ data: { status: true } });

    // Prepare payment update data
    const paymentData = {
      application_id: detailItem.id,
      payment_status: editedPaymentInfo.payment_status,
      txn_id: editedPaymentInfo.txn_id,
      payment_amount: editedPaymentInfo.payment_amount,
    };

    // Execute user update first, then payment update
    updateUserPromise
      .then((userRes) => {
        if (!userRes.data.status) {
          throw new Error(
            userRes.data.message || "Failed to update user account",
          );
        }

        // Now update payment info
        return axios.post("/update-payment-info", paymentData);
      })
      .then((paymentRes) => {
        if (!paymentRes.data.status) {
          throw new Error(
            paymentRes.data.message || "Failed to update payment information",
          );
        }

        setSubmitSuccess("Application information updated successfully!");

        // Clear password fields after successful update
        setPassword("");
        setConfirmPassword("");
        setPasswordError("");

        // Update local state to reflect password status if password was set
        if (password && password.trim()) {
          setDetailItem((prev) => ({
            ...prev,
            hasPassword: true,
          }));
        }

        // Refresh the application data
        fetchApplications({ pageNo: pageNo, perPage: perPage });
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "Failed to update application";
        setSubmitError(errorMsg);
        log.error("Error updating application:", error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [
    detailItem,
    editedFields,
    editedPaymentInfo,
    password,
    validatePassword,
    resetToken,
    fetchApplications,
    pageNo,
    perPage,
  ]);

  // View application details
  const viewDetails = useCallback(
    (item) => {
      log.debug("members: Details view: ", item);
      setDetailItem(item);
      setEditedFields({
        email: item.email_id,
        // Set plan_type to empty string if it's not a valid value
        // This prevents the MUI warning about out-of-range values
        plan_type: item?.plan_type || "",
      });
      setEditedPaymentInfo({
        payment_status: item.payment_status || "",
        txn_id: item.txn_id || "",
        payment_amount: item.payment_amount || "", // Initialize payment_amount
      });
      // Set reset token from user data if it exists
      setResetToken(item.user_data?.reset_token || "");
      setPassword("");
      setConfirmPassword("");
      setPasswordError("");
      setSubmitError("");
      setSubmitSuccess("");
      // Fetch membership payment info
      fetchPayments({
        userId: item.user_data?.id,
        applicationId: item.id,
        email: item.email_id,
      });

      // Fetch membership types when opening details
      fetchOptionData();
    },
    [fetchOptionData, fetchPayments],
  );

  // Close application details
  const closeDetails = useCallback(() => {
    setDetailItem(null);
    setPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setSubmitError("");
    setSubmitSuccess("");
  }, []);

  // Open the existing full member dialog when another admin page links to a
  // member by email. The exact email match prevents a partial search result
  // from opening the wrong application.
  const requestedMemberEmail = searchParams.get("email")?.trim() || "";
  useEffect(() => {
    if (!requestedMemberEmail) {
      memberEmailLookupRef.current = "";
      return undefined;
    }

    if (memberEmailLookupRef.current === requestedMemberEmail) {
      return undefined;
    }

    memberEmailLookupRef.current = requestedMemberEmail;
    let active = true;

    const openRequestedMember = async () => {
      try {
        const response = await getOnlineApplications({
          pageNo: 1,
          perPage: 50,
          searchText: requestedMemberEmail,
          searchType: "email_id",
          showAllPending: true,
        });
        const results = response?.data?.data?.appliactions || [];
        const requestedEmailLower = requestedMemberEmail.toLowerCase();
        const member = results.find(
          (item) =>
            String(item?.email_id || "").trim().toLowerCase() ===
            requestedEmailLower,
        );

        if (!active) {
          return;
        }

        if (member) {
          viewDetails(member);
        } else {
          window.alert(
            `No membership application was found for ${requestedMemberEmail}.`,
          );
        }
      } catch (error) {
        if (active) {
          log.error("Failed to open member details from email:", error);
          window.alert("Unable to load the member details.");
        }
      } finally {
        if (active) {
          setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams);
            nextParams.delete("email");
            return nextParams;
          }, { replace: true });
        }
      }
    };

    openRequestedMember();

    return () => {
      active = false;
    };
  }, [requestedMemberEmail, setSearchParams, viewDetails]);

  // Activate user account
  const activateUserAccount = useCallback(() => {
    setShowConfirmDialog(false);

    // Validate password if provided
    if (password && !validatePassword()) {
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    // Build request data, only include password if it has a value
    const requestData = {
      application_id: detailItem.id,
      email: editedFields.email,
      state: "active", // Explicitly set to active for admin activation
    };

    if (password && password.trim()) {
      requestData.password = password;
    }

    // Include plan_type if it has a value
    if (editedFields?.plan_type && editedFields.plan_type.trim()) {
      requestData.plan_type = editedFields.plan_type;
      // When admin assigns plan_type, set payment_status to complimentary
      requestData.payment_status = "complimentary";
    }

    axios
      .post("/activate-application-user", requestData)
      .then((res) => {
        if (res.data.status) {
          setSubmitSuccess("User account activated successfully!");

          // Clear password fields after successful activation
          setPassword("");
          setConfirmPassword("");
          setPasswordError("");

          // Update local state to reflect new user account status immediately
          setDetailItem((prev) => ({
            ...prev,
            has_user_account: true,
            // Set hasPassword to true if a password was provided
            hasPassword: password && password.trim() ? true : prev.hasPassword,
            user_data: res.data.data || {
              id: res.data.data?.user_id,
              type: "user",
              state: res.data.data?.state || "active",
              created_at: new Date().toISOString(),
            },
          }));

          // Refresh the application data in background
          fetchApplications({ pageNo: pageNo, perPage: perPage });
        } else {
          setSubmitError(res.data.message || "Failed to activate user account");
        }
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.message || "Failed to activate user account";
        setSubmitError(errorMsg);
        log.error("Error activating user account:", error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [
    detailItem,
    editedFields,
    password,
    validatePassword,
    fetchApplications,
    pageNo,
    perPage,
  ]);

  /**
   * Consolidated user state assignment function
   * @param {boolean} param0.state - users.state value (should exist)
   * @param {function} param0.onFinal - Optional callback function to execute after state change
   */
  const setUserState = useCallback(
    ({ state, onFinal = null }) => {
      if (!isString(state)) {
        log.error("[ADMIN] setUserState(): Invalid state value:", state);
        return;
      }

      const userId =
        detailItem?.user_data?.user_id ?? detailItem?.user_data?.id;
      if (!userId) {
        log.error("[ADMIN] setUserState(): Missing user_id", { detailItem });
        setSubmitError("Cannot change user state: missing user ID");
        return;
      }

      setShowArchiveDialog(false);
      setSubmitting(true);
      setSubmitError("");
      setSubmitSuccess("");
      let _action = "inactive";
      let _xPayload = {}; // Additional HTTP payload options for a matched state operation
      switch (state) {
        case "active":
          _action = "activated";
          break;
        case "archived":
          _action = "archived";
          break;
        case "suspended":
          _action = "suspended";
          break;
        case "inactive":
        default:
          _action = "inactive";
          break;
      }
      const _previousState = detailItem.user_data?.state ?? "";

      axios
        .post("/set-user-state", {
          user_id: userId,
          state,
          ..._xPayload,
        })
        .then((res) => {
          if (res.data.status) {
            setSubmitSuccess(`User account state changed: ${_action}`);

            // Update local state to reflect archived status immediately
            setDetailItem((prev) => ({
              ...prev,
              user_data: {
                ...prev.user_data,
                state,
              },
            }));

            // Refresh the application data in background
            fetchApplications({ pageNo: pageNo, perPage: perPage });
          } else {
            setSubmitError(
              res.data.message ||
                `! User account state change failure: ${_previousState} -> ${state}(${_action})`,
            );
          }
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message ||
            `! User account state change failure: ${_previousState} -> ${state}(${_action})`;
          setSubmitError(errorMsg);
          log.error(`Error ${_action} user account:`, error);
        })
        .finally(() => {
          setSubmitting(false);
          if (isFunction(onFinal)) {
            onFinal();
          }
        });
    },
    [detailItem, fetchApplications, pageNo, perPage],
  );

  const deleteUserAccount = useCallback(
    (verifiedEmail) => {
      if (!verifiedEmail) {
        setSubmitError(
          "Failed to delete user account - Email confirmation failed",
        );
        return false;
      }
      setShowDeleteConfirmDialog(false);
      setSubmitting(true);
      setSubmitError("");
      setSubmitSuccess("");
      setEmailVerification("");

      // Make sure user_id exists and is a number
      const rawUserId =
        detailItem?.user_data?.user_id ?? detailItem?.user_data?.id;
      if (!rawUserId) {
        setSubmitError("Cannot delete user: Missing user ID");
        log.debug("Cannot delete user: Missing user ID", { detailItem });
        setSubmitting(false);
        return;
      }

      const userId = parseInt(rawUserId, 10);

      // Log what we're sending for debugging
      log.debug("Sending delete request with user_id:", userId);

      axios
        .post(
          "/delete-user",
          {
            user_id: userId,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )
        .then((res) => {
          if (res.data.status) {
            setSubmitSuccess("User account deleted successfully!");

            // Update local state to reflect deleted status
            setDetailItem((prev) => ({
              ...prev,
              has_user_account: false,
              user_data: null,
            }));

            // Refresh the application data in background
            fetchApplications({ pageNo: pageNo, perPage: perPage });

            // Close dialog after a slight delay
            setTimeout(() => {
              closeDetails();
            }, 2000);
          } else {
            setSubmitError(res.data.message || "Failed to delete user account");
          }
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message || "Failed to delete user account";
          setSubmitError(errorMsg);
          log.error("Error deleting user account:", error);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [detailItem, fetchApplications, pageNo, perPage, closeDetails],
  );

  // function to check if user is suspended
  const isUserSuspended = useCallback(
    () => detailItem?.user_data?.state === "suspended",
    [detailItem?.user_data?.state],
  );

  const userAccountState = useCallback(
    () => detailItem?.user_data?.state,
    [detailItem?.user_data?.state],
  );

  // generate reset token
  const generateResetToken = useCallback(() => {
    setResetToken(nanoid(20)); // Generate a 20-character token
    setShowResetTokenDialog(false);
  }, []);

  // clear reset token
  //const clearResetToken = useCallback(() => setResetToken(""), []);

  // Fix the search handlers to properly update state and trigger search
  const handleSearch = useCallback(() => {
    // Explicitly pass current search state to ensure it's used
    fetchApplications({
      pageNo: 1,
      searchText: searchText,
      searchType: searchType,
    });
  }, [fetchApplications, searchText, searchType]);

  const handleSearchTextChange = useCallback((text) => setSearchText(text), []);

  const handleSearchTypeChange = useCallback((type) => setSearchType(type), []);

  const clearSearch = useCallback(() => {
    setSearchText("");
    setSearchType("email_id");
    // Important: Use setTimeout to ensure state is updated before search
    setTimeout(() => {
      fetchApplications({
        pageNo: 1,
        searchText: "",
        searchType: "email_id",
        showAllPending,
      });
    }, 0);
  }, [fetchApplications, showAllPending]);

  const toggleShowAllPending = useCallback(() => {
    fetchApplications({ pageNo: 1, showAllPending: !showAllPending });
  }, [fetchApplications, showAllPending]);

  // Pagination handlers
  const handlePageChange = (page) => fetchApplications({ pageNo: page });

  const handlePerPageChange = (value) =>
    fetchApplications({ pageNo: 1, perPage: value });

  // Table column configuration
  const tableColumns = [
    {
      field: "selection",
      label: (
        <FormControlLabel
          control={
            <Checkbox
              checked={
                applications.length > 0 &&
                selectedIds.length === applications.length
              }
              indeterminate={
                selectedIds.length > 0 &&
                selectedIds.length < applications.length
              }
              onChange={(e) => handleBulkSelection(e.target.checked)}
            />
          }
          label=""
        />
      ),
      width: "5%",
      compact: true,
      render: (row) => (
        <Tooltip title={`ID: ${row.id}`}>
          <Checkbox
            size="small"
            checked={selectedIds.includes(row.id)}
            onChange={() => handleSelection(row.id)}
            sx={{ m: 0, p: 0 }}
          />
        </Tooltip>
      ),
    },
    {
      field: "status",
      label: "Status",
      width: "9%",
      minWidth: 72,
      compact: true,
      align: "center",
      render: (row) => <UserStatusDot user={row} />,
    },
    {
      field: "name",
      label: "Name",
      width: "28%",
      minWidth: 190,
      render: (row) => (
        <Tooltip
          title={
            <Stack>
              <Box>
                <strong>Name:</strong> {formatName(row) || "Not recorded"}
              </Box>
              <Box>
                <strong>Profession:</strong> {row.profession || "N/A"}
                <br />
                <strong>Specialty: </strong> {row.specialty || "N/A"}
                <br />
                <strong>Subspecialty: </strong> {row.subspecialty || "N/A"}
                <br />
                <strong>Institution: </strong> {row.institution || "N/A"}
              </Box>
            </Stack>
          }
        >
          <Box
            component="span"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formatName(row) || "Not recorded"}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "email_id",
      label: "Email",
      width: "43%",
      minWidth: 230,
      render: (row) => <TableCellCopy value={row.email_id} />,
    },
    {
      field: "actions",
      label: "Actions",
      width: "15%",
      minWidth: 125,
      sticky: "right",
      compact: true,
      render: (row) => (
        <TableActionSplitButton
          onView={() => viewDetails(row)}
          onOpenMenu={(event) => handleSingleDownloadMenuOpen(event, row)}
          menuId="member-row-download-menu"
          menuOpen={singleDownloadMenuAnchor !== null}
        />
      ),
    },
  ];

  // Search options
  const searchOptions = [
    { value: "email_id", label: "Email" },
    { value: "last_name", label: "Last Name" },
    { value: "profession", label: "Profession" },
  ];

  // CSV Export functionality
  const exportToCsv = useCallback((memberData, filename = "members") => {
    if (!memberData) {
      return;
    }

    // Define fields to include in CSV
    const fields = [
      { key: "user_data.id", label: "User ID" },
      { key: "id", label: "Applicant ID" },
      { key: "title", label: "Title" },
      { key: "first_name", label: "First Name" },
      { key: "middle_name", label: "Middle Name" },
      { key: "last_name", label: "Last Name" },
      { key: "email_id", label: "Email" },
      { key: "phone_number", label: "Phone" },
      { key: "profession", label: "Profession" },
      { key: "specialty", label: "Specialty" },
      { key: "subspecialty", label: "Subspecialty" },
      { key: "institution", label: "Institution" },
      { key: "address", label: "Address" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "zip", label: "Zip" },
      { key: "country", label: "Country" },
      { key: "gender", label: "Gender" },
      { key: "pronouns", label: "Pronouns" },
      { key: "ethnicity", label: "Ethnicity" },
      { key: "race", label: "Race" },
      { key: "sexual_orientation", label: "Sexual Orientation" },
      { key: "language", label: "Languages" },
      { key: "income", label: "Income" },
      { key: "plan_type", label: "Admin Membership Type" },
      { key: "created_at", label: "Submission DateTime" },
      { key: "payments.processor", label: "Payment Processor" },
      {
        key: "payments.processor_transaction_id",
        label: "Payment Transaction ID",
      },
      {
        key: "payments.processor_customer_id",
        label: "Payment Processor Customer ID",
      },
      { key: "payments.plan_type", label: "Plan Type" },
      { key: "payments.payment_at", label: "Payment DateTime" },
      { key: "payments.processor_customer_id", label: "Customer ID" },
      { key: "payments.payment_status", label: "Payment Status" },
      { key: "payments.payment_amount", label: "Payment Amount" },
      { key: "payments.currency", label: "Payment Currency" },
      { key: "payments.term.term_label", label: "Membership Term" },
      {
        key: "payments._membership_year",
        label: "Legacy Membership Year (Compat)",
      },
      { key: "payments._expiration_date", label: "Membership Coverage End" },
      { key: "payments.resolution_source", label: "Membership Resolution" },
      {
        key: "has_user_account",
        label: "Has User Account",
        formatter: (val) => (val ? "Yes" : "No"),
      },
      { key: "user_data.type", label: "User Type" },
      { key: "user_data.created_at", label: "User Created DateTime" },
    ];

    // Export as CSV
    exportDataToCsv({
      data: Array.isArray(memberData) ? memberData : [memberData],
      fields,
      filename: filename,
    });
  }, []);

  // Download single application as CSV
  const downloadCsv = useCallback(
    (id) => {
      const item = applications.find((app) => app.id === id);
      if (item) {
        const filename = "SMFA_Member-" + (item.email_id ? item.email_id : id);
        exportToCsv(item, filename);
      }
    },
    [applications, exportToCsv],
  );

  // Download multiple applications as CSV
  const downloadSelectedAsCsv = useCallback(() => {
    if (selectedIds.length === 0) {
      return;
    }

    const selectedApps = applications.filter((app) =>
      selectedIds.includes(app.id),
    );

    exportToCsv(selectedApps, "SMFA_Members");
  }, [selectedIds, applications, exportToCsv]);

  // Download menu handling
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [singleDownloadMenuAnchor, setSingleDownloadMenuAnchor] =
    useState(null);
  const [singleDownloadItem, setSingleDownloadItem] = useState(null);

  const handleDownloadMenuOpen = (event) =>
    setDownloadMenuAnchor(event.currentTarget);

  const handleDownloadMenuClose = () => setDownloadMenuAnchor(null);

  // Download every application matching the current list filters, not just the
  // records loaded for the current page.
  const downloadAllAsCsv = useCallback(async () => {
    setDownloadAllLoading(true);
    try {
      const res = await getOnlineApplications({
        pageNo: 1,
        perPage: "all",
        searchText,
        searchType,
        showAllPending,
      });

      if (!res?.data?.status) {
        throw new Error(res?.data?.message || "Failed to load all members");
      }

      const allApplications = res.data.data?.appliactions || [];
      exportToCsv(allApplications, "SMFA_Members_All");
      setDownloadMenuAnchor(null);
    } catch (error) {
      log.error("Failed to download all members as CSV:", error);
    } finally {
      setDownloadAllLoading(false);
    }
  }, [exportToCsv, searchText, searchType, showAllPending]);

  const downloadAllAsZip = useCallback(async () => {
    setDownloadAllZipLoading(true);
    setDownloadMenuAnchor(null);
    setPdfDownloadLoading(true);
    setPdfDownloadMessage("Loading members and preparing PDF ZIP...");

    try {
      const res = await getOnlineApplications({
        pageNo: 1,
        perPage: "all",
        searchText,
        searchType,
        showAllPending,
      });

      if (!res?.data?.status) {
        throw new Error(res?.data?.message || "Failed to load all members");
      }

      const applicationIds = (res.data.data?.appliactions || [])
        .map((application) => application.id)
        .filter(Boolean);
      if (applicationIds.length > 0) {
        await downloadApplicationsZip(
          applicationIds,
          zipDownloadFilename("Members", { all: true }),
        );
      }
    } catch (error) {
      log.error("Failed to download all member PDFs as ZIP:", error);
    } finally {
      setDownloadAllZipLoading(false);
      setPdfDownloadLoading(false);
      setPdfDownloadMessage("");
    }
  }, [downloadApplicationsZip, searchText, searchType, showAllPending]);

  const handleDownloadSelectedAsZip = useCallback(() => {
    downloadSelectedAsZip();
    setDownloadMenuAnchor(null);
  }, [downloadSelectedAsZip]);

  const handleSingleDownloadMenuOpen = (event, item) => {
    setSingleDownloadItem(item);
    setSingleDownloadMenuAnchor(event.currentTarget);
  };

  const handleSingleDownloadMenuClose = () => {
    setSingleDownloadMenuAnchor(null);
    setSingleDownloadItem(null);
  };

  /** Payment raw details dialog */
  const [showRawPaymentDialog, setShowRawPaymentDialog] = useState(false);
  const handleRawPaymentDialog = () =>
    setShowRawPaymentDialog(!showRawPaymentDialog);

  /**
   * User status dot component - Shows a dot with the user's status in a defined color.
   * Also includes a tooltip with detailed information about the user.
   * @param {object} param0 - User data object {...online_application.*, user_data: {...users.*}, payments: {...payments.*}}
   * @returns {JSX.Element}
   * @component
   */
  const UserStatusDot = ({ user }) => {
    if (!user) {
      return null;
    }

    const hasCurrentActiveMembership = isCurrentActiveMembershipPayment(
      user?.payments,
    );
    const hasAdminAssignedMembership =
      String(user?.plan_type).startsWith("membership_") &&
      user?.payment_status === "complimentary";

    let _color = "grey";
    let _variant = "filled";
    let _title = "Unknown";
    switch (true) {
      case user?.user_data?.type === "admin":
        _title = "Admin";
        _color = "primary";
        _variant = "filled";
        break;
      case hasCurrentActiveMembership && user?.user_data?.state === "active":
        _title = "Membership: Active";
        _color = "success";
        _variant = "filled";
        break;
      case hasAdminAssignedMembership:
        _title = "Membership: Active (Administratively assigned)";
        _color = "success";
        _variant = "outlined";
        break;
      case toInteger(user?.payments?.price) === 0 &&
        user?.user_data?.state === "inactive":
        _title = "Membership: Pending Free";
        _color = "grey";
        _variant = "filled";
        break;
      case user?.user_data?.state === "suspended":
        _title = "Membership: Suspended";
        _color = "warning";
        _variant = "outlined";
        break;
      case user?.payments?._is_expired === true:
        _title = "Membership: Expired";
        _color = "secondary";
        _variant = "outlined";
        break;
      default:
        break;
    }

    const _tooltipTitle = (
      <>
        <Typography variant="body2" gutterBottom>
          {_title}
        </Typography>
        <Stack direction="column">
          <Box>
            <strong>Paid Membership</strong>:{" "}
            {user?.payments?.plan_type || "N/A"}
          </Box>
          <Box>
            <strong>Expiration Date</strong>:{" "}
            {formatDate(user?.payments?._expiration_date) || "N/A"}
          </Box>
          <Box>
            <strong>Membership Term</strong>:{" "}
            {getMembershipTermLabel(user?.payments)}
          </Box>
          <Box>
            <strong>Legacy Compatibility Year</strong>:{" "}
            {shouldShowLegacyMembershipYear(user?.payments)
              ? getLegacyMembershipYearLabel(user?.payments)
              : "N/A"}
          </Box>
          <Box>
            <strong>Resolution</strong>:{" "}
            {getMembershipResolutionLabel(user?.payments)}
          </Box>
          <Box>
            <strong>Admin-assigned Membership</strong>:{" "}
            {user?.plan_type && user?.payment_status === "complimentary"
              ? user.plan_type
              : "N/A"}
          </Box>
          <Box>
            <strong>User Type</strong>: {user?.user_data?.type || "N/A"}
          </Box>
          <Box>
            <strong>User State</strong>: {user?.user_data?.state || "N/A"}
          </Box>
          <Box>
            <strong>User Created on </strong>:{" "}
            {formatDate(user?.user_data?.created_at) || "N/A"}
          </Box>
          <Box>
            <strong>Application Submitted on</strong>:{" "}
            {formatDate(user?.created_at) || "N/A"}
          </Box>
          <Box>
            <strong>User ID</strong>: {user?.user_data?.id || "N/A"} /
            <strong>Application ID</strong>: {user?.id || "N/A"} /
            <strong>Last Payment ID</strong>: {user?.payments?.id || "N/A"}
          </Box>
        </Stack>
      </>
    );

    const hasPaidMembership = isCurrentActivePaidMembership(user?.payments);

    return (
      <Box sx={{ ...cssTimelineDotLegendBox, gap: 0.25 }}>
        <Tooltip title={_tooltipTitle}>
          <TimelineDot color={_color} variant={_variant} />
        </Tooltip>
        {hasPaidMembership ? (
          <Tooltip title={`Paid plan: ${user?.payments?.plan_type}`}>
            <AttachMoneyIcon
              color="success"
              sx={{ fontSize: 15, lineHeight: 1 }}
            />
          </Tooltip>
        ) : null}
      </Box>
    );
  };

  // MAIN Members() render
  return (
    <ToolBody title="Member Management">
      <PdfDownloadBackdrop
        open={pdfDownloadLoading}
        message={pdfDownloadMessage}
      />
      {/* Top actions */}
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-start",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <ButtonGroup variant="contained" color="primary">
          <Button
            startIcon={<FileDownloadIcon />}
            disabled={
              downloadDisabled ||
              downloadAllLoading ||
              downloadAllZipLoading ||
              pdfDownloadLoading
            }
            onClick={downloadSelectedAsCsv}
          >
            Download Selected ({selectedIds.length})
          </Button>
          <Button
            size="small"
            aria-label="Open member download options"
            aria-controls={downloadMenuAnchor ? "member-download-menu" : undefined}
            aria-expanded={downloadMenuAnchor ? "true" : undefined}
            aria-haspopup="menu"
            disabled={
              totalCount === 0 ||
              downloadAllLoading ||
              downloadAllZipLoading ||
              pdfDownloadLoading
            }
            onClick={handleDownloadMenuOpen}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Tooltip title="Default hides older payment-pending items; application-pending rows always show">
          <Button
            variant={showAllPending ? "contained" : "outlined"}
            color={showAllPending ? "warning" : "inherit"}
            startIcon={
              showAllPending ? <FilterAltOffIcon /> : <FilterAltIcon />
            }
            onClick={toggleShowAllPending}
          >
            {showAllPending ? "Show All Pending: On" : "Show All Pending: Off"}
          </Button>
        </Tooltip>
      </Stack>

      {/* Download Menu */}
      <Menu
        id="member-download-menu"
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={downloadAllAsCsv} disabled={downloadAllLoading}>
          <ListItemIcon>
            <TableChartIcon />
          </ListItemIcon>
          <ListItemText primary="Download All as CSV" />
        </MenuItem>
        <MenuItem
          onClick={downloadAllAsZip}
          disabled={downloadAllZipLoading || pdfDownloadLoading}
        >
          <ListItemIcon>
            <PictureAsPdfIcon />
          </ListItemIcon>
          <ListItemText primary="Download All as ZIP (PDF)" />
        </MenuItem>
        <MenuItem
          onClick={handleDownloadSelectedAsZip}
          disabled={downloadDisabled || pdfDownloadLoading}
        >
          <ListItemIcon>
            <FileDownloadIcon />
          </ListItemIcon>
          <ListItemText primary="Download Selected as ZIP (PDF)" />
        </MenuItem>
      </Menu>

      {/* Search Controls */}
      <SearchControls
        searchText={searchText}
        searchType={searchType}
        searchOptions={searchOptions}
        onSearchChange={handleSearchTextChange}
        onTypeChange={handleSearchTypeChange}
        onSubmit={handleSearch}
        onClear={clearSearch}
        perPage={perPage}
        totalCount={totalCount}
        onPerPageChange={handlePerPageChange}
      />

      <Box>
        {/* @see <UserStatusDot> */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-start",
            alignItems: "center",
            border: "1px dashed #AAA",
            p: 1,
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 1,
          }}
        >
          <Typography
            variant="body1"
            color="primary"
            sx={{ flexBasis: { xs: "100%", sm: "auto" } }}
          >
            Status Legend:
          </Typography>
          <Box sx={cssTimelineDotLegendBox}>
            <TimelineDot color="primary" variant="filled" />
            &nbsp; Admin
          </Box>
          <Box sx={cssTimelineDotLegendBox}>
            <TimelineDot color="success" variant="filled" />
            &nbsp;
            <Typography>Active</Typography>
          </Box>
          <Box sx={cssTimelineDotLegendBox}>
            <TimelineDot color="success" variant="outlined" />
            &nbsp; Active (No Membership)
          </Box>
          <Box sx={cssTimelineDotLegendBox}>
            <TimelineDot color="grey" variant="filled" />
            &nbsp; Pending Free/Misc
          </Box>
          <Box sx={cssTimelineDotLegendBox}>
            <TimelineDot color="warning" variant="outlined" />
            &nbsp; Suspended
          </Box>
          <Box sx={cssTimelineDotLegendBox}>
            <TimelineDot color="secondary" variant="outlined" />
            &nbsp; Expired
          </Box>
          <Box sx={cssTimelineDotLegendBox}>
            <AttachMoneyIcon color="success" sx={{ fontSize: 15 }} />
            &nbsp; Paid Plan
          </Box>
        </Stack>
      </Box>

      {/* Data Table */}
      <ResponsiveDataTable
        minWidth={820}
        refreshKey={`${applications.length}-${loading}`}
      >
        <DataTable
          columns={tableColumns}
          data={applications}
          loading={loading}
          sortConfig={null}
          keyField="id"
          responsive
        />
      </ResponsiveDataTable>

      {/* Pagination */}
      {totalPage > 1 && (
        <TablePagination
          page={pageNo}
          totalPages={totalPage}
          loading={loading}
          onChange={handlePageChange}
          entriesPerPage={perPage}
          onEntriesChange={handlePerPageChange}
        />
      )}

      {/* Details Dialog */}
      <StyledDialog
        open={detailItem !== null}
        onClose={closeDetails}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Member Details -{" "}
          {detailItem?.user_data?.email ||
            detailItem?.email_id ||
            "Unknown email"}
          <IconButton
            aria-label="close"
            onClick={closeDetails}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {detailItem && (
            <>
              {/* Status messages */}
              {submitSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {submitSuccess}
                </Alert>
              )}
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}

              {/* Core Information Section */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" gutterBottom color="primary">
                  Core Information
                </Typography>
                <Tooltip title="View Raw Data">
                  <IconButton
                    size="small"
                    onClick={() => setShowRawDataDialog(true)}
                    color="primary"
                  >
                    <DataIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                {/* FIRST: NON-EDITABLE FIELDS */}
                {/* Name Field - NOT Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">
                      {formatName(detailItem)}
                    </Typography>
                  </Box>
                </Grid>

                {/* Phone Field - NOT Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {detailItem.phone_number || "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Birth Date Field - NOT Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Birth Date
                    </Typography>
                    <Typography variant="body1">
                      {detailItem.dob_display || "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Submission Date - NOT Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Application Submitted On
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(detailItem.created_at) || "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Status Alerts in Grid Layout */}
                <Grid size={{ xs: 12 }}>
                  <Grid container spacing={2}>
                    {/* Is Admin? */}
                    {detailItem?.user_data?.type === "admin" && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Alert severity="warning">Is an Admin</Alert>
                      </Grid>
                    )}

                    {/* User Account Status */}
                    {detailItem.has_user_account && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Alert severity="info">Has active user account</Alert>
                      </Grid>
                    )}

                    {/* Password status */}
                    {!detailItem?.hasPassword && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Alert severity="error">
                          No assigned password (empty)
                        </Alert>
                      </Grid>
                    )}

                    {/* Password Reset Token Warning */}
                    {detailItem.user_data?.reset_token && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Alert severity="warning">
                          Password reset token pending
                        </Alert>
                      </Grid>
                    )}

                    {/* Membership Information */}
                    {(detailItem?.plan_type?.length > 0 ||
                      (detailItem?.payments &&
                        detailItem?.payments?.plan_type)) && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Alert severity="success">
                          Has membership (
                          {detailItem.plan_type &&
                            detailItem.payment_status === "complimentary" &&
                            `Admin-Assigned: ${detailItem.plan_type}`}
                          {detailItem.plan_type &&
                            detailItem?.payment_status === "complimentary" &&
                            detailItem?.payments &&
                            detailItem?.payments?.plan_type &&
                            ", "}
                          {detailItem?.payments &&
                          detailItem?.payments?.plan_type &&
                          detailItem?.payments?.payment_status === "succeeded"
                            ? `Purchased: ${detailItem?.payments.plan_type}`
                            : `Pending Purchase: ${detailItem?.payments?.plan_type || "N/A"}`}
                          )
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                {/* User Account Data Display if available */}
                {detailItem.user_data && (
                  <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Account Created
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(detailItem.user_data.created_at) ||
                              "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Account Updated
                          </Typography>
                          <Typography variant="body1">
                            {formatDate(detailItem.user_data.updated_at) ||
                              "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* Payment Information */}
                {detailItem?.payments ? (
                  <Grid size={12}>
                    <Card
                      variant="outlined"
                      sx={{
                        mb: 2,
                        backgroundColor:
                          detailItem?.payments?.payment_status === "succeeded"
                            ? "#e8f5e9" // very light green
                            : detailItem?.payments?.payment_status === "pending"
                              ? "#fff8e1" // very light orange
                              : "#f5f5f5", // very light grey
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6" component="div">
                            Payment Details
                          </Typography>
                          <Chip
                            label={
                              detailItem.payments.payment_status
                                ? detailItem.payments.payment_status
                                    .charAt(0)
                                    .toUpperCase() +
                                  detailItem.payments.payment_status.slice(1)
                                : "Unknown"
                            }
                            color={
                              detailItem.payments.payment_status === "succeeded"
                                ? "success"
                                : detailItem.payments.payment_status ===
                                    "pending"
                                  ? "warning"
                                  : detailItem.payments.payment_status ===
                                      "failed"
                                    ? "error"
                                    : "default"
                            }
                          />
                        </Box>

                        <Grid container spacing={1}>
                          {/* Row 1: Processor info */}
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Processor
                            </Typography>
                            <Typography variant="body1">
                              {detailItem.payments.processor || "N/A"}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Transaction ID
                            </Typography>
                            <Typography variant="body1">
                              {detailItem.payments?.processor_transaction_id ||
                                "N/A"}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Customer ID
                            </Typography>
                            <Typography variant="body1">
                              {detailItem.payments?.processor_customer_id ||
                                "N/A"}
                            </Typography>
                          </Grid>

                          {/* Row 2: Plan & amount */}
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Plan Type
                            </Typography>
                            <Typography variant="body1">
                              {detailItem?.payments?.plan_type || "N/A"}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Amount
                            </Typography>
                            <Typography variant="body1">
                              {detailItem.payments?.payment_amount
                                ? `${detailItem.payments?.payment_amount} ${(detailItem.payments?.currency || "USD").toUpperCase()}`
                                : "N/A"}
                            </Typography>
                          </Grid>

                          {(detailItem.payments?.term?.term_label ||
                            detailItem.payments?._membership_year) && (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Membership Term
                              </Typography>
                              <Typography variant="body1">
                                {getMembershipTermLabel(detailItem.payments)}
                              </Typography>
                            </Grid>
                          )}

                          {shouldShowLegacyMembershipYear(
                            detailItem.payments,
                          ) && (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Legacy Compatibility Year
                                </Typography>
                                <Typography variant="body1">
                                  {getLegacyMembershipYearLabel(
                                    detailItem.payments,
                                  )}
                                </Typography>
                              </Grid>
                            )}

                          {detailItem.payments?.resolution_source && (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Membership Resolution
                              </Typography>
                              <Typography variant="body1">
                                {getMembershipResolutionLabel(
                                  detailItem.payments,
                                )}
                              </Typography>
                            </Grid>
                          )}

                          {/* Row 3: Dates */}
                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Payment Date
                            </Typography>
                            <Typography variant="body1">
                              {detailItem.payments.payment_at
                                ? formatDate(detailItem.payments.payment_at, {
                                    separator: "/",
                                  })
                                : "N/A"}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Created Date
                            </Typography>
                            <Typography variant="body1">
                              {detailItem.payments.created_at
                                ? formatDate(detailItem.payments.created_at, {
                                    separator: "/",
                                  })
                                : "N/A"}
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Updated Date
                            </Typography>
                            <Typography variant="body1">
                              {detailItem.payments.updated_at
                                ? formatDate(detailItem.payments.updated_at, {
                                    separator: "/",
                                  })
                                : "N/A"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                      <CardActions>
                        <Button
                          variant="outlined"
                          onClick={handleRawPaymentDialog}
                          startIcon={<DataIcon />}
                        >
                          View Raw Transaction Data
                        </Button>
                      </CardActions>
                    </Card>
                    <Dialog open={showRawPaymentDialog} fullWidth>
                      <DialogTitle>Raw Transaction Data</DialogTitle>
                      <DialogContent>
                        <TextField
                          multiline
                          rows={16}
                          variant="outlined"
                          placeholder="Loading..."
                          value={JSON.stringify(detailItem.payments, null, 3)}
                          fullWidth
                          slotProps={{
                            input: {
                              readOnly: true,
                            },
                          }}
                        />
                      </DialogContent>
                      <DialogActions>
                        <Button
                          variant="outlined"
                          onClick={handleRawPaymentDialog}
                          startIcon={<CloseIcon />}
                        >
                          Close
                        </Button>
                      </DialogActions>
                    </Dialog>

                    {/* Raw Data Dialog */}
                    <Dialog open={showRawDataDialog} fullWidth maxWidth="md">
                      <DialogTitle>User API Data</DialogTitle>
                      <DialogContent>
                        <TextField
                          multiline
                          rows={20}
                          variant="outlined"
                          placeholder="Loading..."
                          value={JSON.stringify(detailItem, null, 2)}
                          fullWidth
                          onClick={copyRawDataToClipboard}
                          slotProps={{
                            input: {
                              readOnly: true,
                              style: { cursor: "pointer" },
                            },
                          }}
                          helperText="Click anywhere in the text area to copy data to clipboard"
                        />
                      </DialogContent>
                      <DialogActions>
                        <Button
                          variant="outlined"
                          onClick={() => setShowRawDataDialog(false)}
                          startIcon={<CloseIcon />}
                        >
                          Close
                        </Button>
                        <Button
                          variant="contained"
                          onClick={copyRawDataToClipboard}
                          startIcon={<ContentCopyIcon />}
                        >
                          Copy to Clipboard
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Grid>
                ) : (
                  <Grid size={12}>
                    <em>Payment Information not present</em>
                  </Grid>
                )}

                {/* SEPARATOR: Visual divider between non-editable and editable fields */}
                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>

                {/* SECOND: EDITABLE FIELDS */}
                {/* Email Field - Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email"
                    name="application_email"
                    id="application_email_field"
                    value={editedFields.email}
                    onChange={handleInputChange}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    slotProps={{
                      htmlInput: {
                        autoComplete: "off",
                      },
                    }}
                  />
                </Grid>

                {/* Membership Type Field - Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Membership Type (Admin-Assigned)</InputLabel>
                    <Select
                      name="plan_type"
                      value={fetchingTypes ? "" : editedFields?.plan_type}
                      label="Membership Type"
                      onChange={handleInputChange}
                      disabled={fetchingTypes}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {membershipTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {`${type.label} (${type.value})`}
                        </MenuItem>
                      ))}
                    </Select>
                    {fetchingTypes && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        <Typography variant="caption">
                          Loading membership types...
                        </Typography>
                      </Box>
                    )}
                  </FormControl>
                </Grid>

                {/* Password Fields Header */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Account Password
                  </Typography>
                </Grid>

                {/* Password Field - Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    name="new_account_password"
                    id="new_account_password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      // Validate passwords if confirm field has a value
                      if (confirmPassword) {
                        setTimeout(() => validatePassword(), 0);
                      }
                    }}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    autoComplete="new-password"
                    helperText={
                      !detailItem.has_user_account
                        ? "Required to activate account"
                        : "Leave blank to keep current password"
                    }
                    slotProps={{
                      input: {
                        endAdornment: (
                          <>
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                            {password && password === confirmPassword && (
                              <IconButton
                                onClick={copyPasswordToClipboard}
                                edge="end"
                                title="Copy password"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            )}
                          </>
                        ),
                      },
                    }}
                  />
                </Grid>

                {/* Confirm Password Field - Editable */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Confirm Password"
                    type={showPassword ? "text" : "password"}
                    name="confirm_account_password"
                    id="confirm_account_password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    onBlur={handleConfirmPasswordBlur}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    autoComplete="new-password"
                    error={Boolean(passwordError)}
                    helperText={passwordError || " "}
                  />
                </Grid>

                {/* Password reset token - disable for now */}
                {/* {detailItem.has_user_account && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Password Reset Token
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <TextField
                          label="Reset Password Token"
                          value={resetToken}
                          margin="dense"
                          variant="outlined"
                          slotProps={{
                            input: {
                              readOnly: true,
                              sx: { bgcolor: "rgba(0, 0, 0, 0.05)" }, // Darker background
                              endAdornment: resetToken ? (
                                <IconButton
                                  onClick={copyTokenToClipboard}
                                  edge="end"
                                  title="Copy token"
                                >
                                  <ContentCopyIcon />
                                </IconButton>
                              ) : null,
                            },
                          }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => setShowResetTokenDialog(true)}
                          sx={{ mt: 1 }}
                          size="small"
                        >
                          Generate
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={clearResetToken}
                          sx={{ mt: 1 }}
                          size="small"
                          disabled={!resetToken}
                        >
                          Clear
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Generate a reset token for password recovery, then
                        update user.
                      </Typography>
                    </Grid>
                  </>
                )} */}
              </Grid>

              {/* Extra Information Section (Accordion) */}
              <Box sx={{ mt: 4 }}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="extra-information-content"
                    id="extra-information-header"
                  >
                    <Typography variant="h6" color="primary">
                      Extra Information
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      {/* Institution */}
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Institution
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.institution || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Address */}
                      <Grid size={{ xs: 12 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Address
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.address || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Professional Information */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Professional Information
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Profession
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.profession || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Specialty
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.specialty || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Subspecialty
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.subspecialty || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Languages
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.language || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Income (Low/Middle-Income Country)
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.income || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Demographic Information */}
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Demographic Information
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Race
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.race || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Ethnicity
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.ethnicity || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Gender
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.gender || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Pronouns
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.pronouns || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Sexual Orientation
                          </Typography>
                          <Typography variant="body1">
                            {detailItem.sexual_orientation || "N/A"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
              >
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<TableChartIcon />}
                    onClick={() => downloadCsv(detailItem.id)}
                  >
                    Download CSV
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => downloadPdf(detailItem.id, detailItem)}
                  >
                    Download PDF
                  </Button>

                  <Box
                    sx={{
                      border: "1px solid #d32f2f",
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: "rgba(211, 47, 47, 0.1)",
                    }}
                  >
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {detailItem?.has_user_account ? (
                        <Tooltip
                          placement="top"
                          title={
                            detailItem?.user_data?.type === "admin"
                              ? "Administrator accounts can only be deleted by a database administrator"
                              : "PERMANENTLY delete this account"
                          }
                        >
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => setShowDeleteDialog(true)}
                              disabled={
                                loading ||
                                detailItem?.user_data?.type === "admin"
                              }
                            >
                              Delete Account
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                      {detailItem?.has_user_account ? (
                        <>
                          {userAccountState() !== "archived" ? (
                            <Button
                              variant="outlined"
                              size="small"
                              color="warning"
                              startIcon={<ArchiveIcon />}
                              onClick={() => setShowArchiveDialog(true)}
                              disabled={loading}
                            >
                              Archive Account
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<UnarchiveIcon />}
                              onClick={() => setShowArchiveDialog(true)}
                              disabled={loading}
                            >
                              UN-Archive Account
                            </Button>
                          )}
                          {isUserSuspended() ? (
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<PersonAddIcon />}
                              onClick={() => setShowSuspendDialog(true)}
                              disabled={submitting}
                            >
                              Un-Suspend Account
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              startIcon={<BlockIcon />}
                              onClick={() => setShowSuspendDialog(true)}
                              disabled={submitting}
                            >
                              Suspend Account
                            </Button>
                          )}
                        </>
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>

                <Box sx={{ display: "flex", gap: 2 }}>
                  {detailItem.has_user_account &&
                  detailItem?.user_data?.state !== "inactive" ? (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={updateUserAccount}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Update User"
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => setShowConfirmDialog(true)}
                      disabled={
                        submitting
                        //|| !password || password !== confirmPassword
                      }
                    >
                      {submitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Activate User Account"
                      )}
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Add the confirmation dialogs */}
              <Dialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
              >
                <DialogTitle>Confirm Account Activation</DialogTitle>
                <MuiDialogContent>
                  {detailItem?.payments?.payment_status === "pending" && (
                    <Alert
                      severity="warning"
                      sx={{ mb: 2 }}
                      icon={<WarningAmberOutlinedIcon color="warning" />}
                    >
                      This account has a "pending" payment status! This will
                      give the user membership regardless of their payment
                      status. Only do this if the user has paid through other
                      methods.
                    </Alert>
                  )}
                  <DialogContentText>
                    Are you sure you want to activate this user account?
                  </DialogContentText>
                </MuiDialogContent>
                <DialogActions>
                  <Button onClick={() => setShowConfirmDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={activateUserAccount}
                    color="primary"
                    autoFocus
                  >
                    Activate
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={showSuspendDialog}
                onClose={() => setShowSuspendDialog(false)}
              >
                <DialogTitle>
                  {isUserSuspended()
                    ? "Confirm Account Reactivation"
                    : "Confirm Account Suspension"}
                </DialogTitle>
                <MuiDialogContent>
                  <DialogContentText>
                    {isUserSuspended()
                      ? "Are you sure you want to reactivate this user account? This will restore the user's ability to log in."
                      : "Are you sure you want to suspend this user account? This will prevent the user from logging in until reactivated."}
                  </DialogContentText>
                </MuiDialogContent>
                <DialogActions>
                  <Button onClick={() => setShowSuspendDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() =>
                      setUserState({
                        state: isUserSuspended() ? "active" : "suspended",
                        onFinal: () => setShowSuspendDialog(false),
                      })
                    }
                    color={isUserSuspended() ? "success" : "error"}
                    autoFocus
                  >
                    {isUserSuspended() ? "Reactivate" : "Suspend"}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Add confirmation dialog for reset token */}
              <Dialog
                open={showResetTokenDialog}
                onClose={() => setShowResetTokenDialog(false)}
              >
                <DialogTitle>Generate Reset Token</DialogTitle>
                <MuiDialogContent>
                  <DialogContentText>
                    Click OK to generate a token for password reset, then click
                    "Update User" to save it. This will allow the user to reset
                    their password.
                  </DialogContentText>
                </MuiDialogContent>
                <DialogActions>
                  <Button onClick={() => setShowResetTokenDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={generateResetToken}
                    color="primary"
                    autoFocus
                  >
                    OK
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Replace both snackbars with this version that doesn't trigger warnings */}
              <Snackbar
                open={copySuccess}
                autoHideDuration={2000}
                onClose={() => setCopySuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <Alert
                  severity="success"
                  variant="filled"
                  onClose={() => setCopySuccess(false)}
                >
                  Password copied to clipboard
                </Alert>
              </Snackbar>

              <Snackbar
                open={tokenCopySuccess}
                autoHideDuration={2000}
                onClose={() => setTokenCopySuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <Alert
                  severity="success"
                  variant="filled"
                  onClose={() => setTokenCopySuccess(false)}
                >
                  Token copied to clipboard
                </Alert>
              </Snackbar>

              <Snackbar
                open={rawDataCopySuccess}
                autoHideDuration={2000}
                onClose={() => setRawDataCopySuccess(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <Alert
                  severity="success"
                  variant="filled"
                  onClose={() => setRawDataCopySuccess(false)}
                >
                  Raw data copied to clipboard
                </Alert>
              </Snackbar>

              {/* Archive/Unarchive Confirmation Dialog */}
              <Dialog
                open={showArchiveDialog}
                onClose={() => setShowArchiveDialog(false)}
              >
                <DialogTitle>
                  {userAccountState() === "archived"
                    ? "Confirm Account Unarchiving"
                    : "Confirm Account Archiving"}
                </DialogTitle>
                <MuiDialogContent>
                  <DialogContentText component="div">
                    <Typography variant="body1" color="warning">
                      <InfoIcon />
                      &nbsp; About Archiving (Action:{" "}
                      {userAccountState() === "archived"
                        ? "Unarchiving"
                        : "Archiving"}
                      )
                    </Typography>
                    (Un-)Archiving a user account is akin to a soft-delete
                    operation. Archived accounts cannot log in, and the account
                    will not appear in default administrative user lists.
                    However, the account will still exist in the database and
                    can be un-archived later if needed.
                  </DialogContentText>
                </MuiDialogContent>
                <DialogActions>
                  <Button onClick={() => setShowArchiveDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      () =>
                        setUserState({
                          state:
                            userAccountState() === "archived"
                              ? "active"
                              : "archived",
                        })
                      // userAccountState() === "archived"
                      //   ? unArchiveUserAccount
                      //   : archiveUserAccount
                    }
                    color={
                      userAccountState() === "archived" ? "primary" : "warning"
                    }
                    autoFocus
                  >
                    {userAccountState() === "archived"
                      ? "Un-Archive"
                      : "Archive"}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Delete Account First Confirmation - Email Verification */}
              <Dialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Verify Email to Delete Account</DialogTitle>
                <MuiDialogContent>
                  <DialogContentText sx={{ mb: 2 }}>
                    To confirm deletion of this user account, please type the
                    user's email address:
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: "bold",
                        display: "block",
                        mt: 1,
                      }}
                    >
                      {detailItem?.user_data?.email || detailItem.email_id}
                    </Typography>
                  </DialogContentText>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Type email to confirm"
                    type="email"
                    fullWidth
                    value={emailVerification}
                    onChange={(e) => setEmailVerification(e.target.value)}
                    error={Boolean(emailVerificationError)}
                    helperText={emailVerificationError}
                    variant="outlined"
                  />
                </MuiDialogContent>
                <DialogActions>
                  <Button
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setEmailVerification("");
                      setEmailVerificationError("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      const accountEmail =
                        detailItem?.user_data?.email || detailItem.email_id;
                      if (
                        emailVerification.trim().toLowerCase() !==
                        (accountEmail || "").trim().toLowerCase()
                      ) {
                        setEmailVerificationError("Email does not match");
                      } else {
                        setShowDeleteDialog(false);
                        setShowDeleteConfirmDialog(true);
                      }
                    }}
                    color="error"
                    variant="contained"
                    disabled={!emailVerification}
                  >
                    Verify
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Delete Account Final Confirmation */}
              <Dialog
                open={showDeleteConfirmDialog}
                onClose={() => setShowDeleteConfirmDialog(false)}
              >
                <DialogTitle>
                  Final Confirmation: Delete User Account
                </DialogTitle>
                <MuiDialogContent>
                  <Typography
                    variant="body1"
                    color="error"
                    sx={{ fontWeight: "bold", mb: 2 }}
                  >
                    Warning: This action cannot be undone!
                  </Typography>
                  <DialogContentText>
                    Are you absolutely sure you want to permanently delete this
                    user account? This will remove all user data from the
                    system.
                  </DialogContentText>
                </MuiDialogContent>
                <DialogActions>
                  <Button onClick={() => setShowDeleteConfirmDialog(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => deleteUserAccount(emailVerification)}
                    color="error"
                    variant="contained"
                    autoFocus
                  >
                    Delete Permanently
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </DialogContent>
      </StyledDialog>

      {/* Single Download Menu */}
      <Menu
        id="member-row-download-menu"
        anchorEl={singleDownloadMenuAnchor}
        open={Boolean(singleDownloadMenuAnchor)}
        onClose={handleSingleDownloadMenuClose}
      >
        <MenuItem
          onClick={() => {
            downloadCsv(singleDownloadItem?.id);
            handleSingleDownloadMenuClose();
          }}
        >
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            downloadPdf(singleDownloadItem?.id, singleDownloadItem);
            handleSingleDownloadMenuClose();
          }}
        >
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
      </Menu>
    </ToolBody>
  );
};

export default Members;
